import { Avatar, Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { VaultIcon, PlusIcon } from '@/components/icons';
import { Tooltip } from '@/components/Tooltip';
import type { DirectChannel } from '@/types/api';

/**
 * Section-header "+ Add" button — used by channel categories and the
 * DM list to launch the create flow for that section. Renders a real
 * HeroUI Button under the heroui skin; the Aether markup is preserved.
 */
export function SectionAddButton(props: {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      fullWidth
      className="justify-start gap-2 text-default-500"
      onPress={props.onClick}
    >
      {props.icon ?? <PlusIcon className="h-3.5 w-3.5" />}
      {props.label}
    </Button>
  );
}

/**
 * Saved-messages row in the DM-list. Permanent pinned entry that opens
 * the bookmarks modal — Telegram-style "Сохранённое" affordance.
 */
export function SavedShortcut(props: { onClick: () => void; active?: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-current={props.active ? 'true' : undefined}
      className={
        'mb-0.5 flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ' +
        (props.active ? 'bg-default/70 text-foreground' : 'text-foreground/90 hover:bg-default/40')
      }
    >
      {/* Match the DM avatar size and use the theme's contrasting accent ink. */}
      <Avatar size="sm" className="studio-vault-avatar shrink-0">
        <Avatar.Fallback className="bg-accent text-accent-foreground">
          <VaultIcon className="h-[18px] w-[18px]" />
        </Avatar.Fallback>
      </Avatar>
      <span className="min-w-0 flex-1 truncate text-sm">{t('ui.saved.title')}</span>
    </button>
  );
}

/**
 * Round 40×40 footer icon button (mic / deafen / call hang-up / settings
 * gear). Variants for "danger" (red, for hang-up) and "active" (cyan,
 * for the open settings modal). Rest state has NO background — bg only
 * appears on hover or in non-default variants.
 */
export function FooterIconButton(props: {
  title: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  active?: boolean;
}) {
  return (
    <Tooltip label={props.title}>
      <Button
        isIconOnly
        size="md"
        variant={props.danger ? 'danger' : props.active ? 'secondary' : 'ghost'}
        isDisabled={props.disabled}
        onPress={props.onClick}
        aria-label={props.title}
      >
        {props.children}
      </Button>
    </Tooltip>
  );
}

/**
 * Compute the display label for a DM channel. For group DMs use the
 * configured name; if none, fall back to a comma-joined "@first three
 * others" preview. For 1-on-1 DMs, just show the other participant's
 * displayName / username.
 */
/** The personal "Сейф" (self-chat, Telegram Saved Messages): a DM channel whose
 *  only participant is you. The server creates exactly one such DM per user. */
export function isSelfVault(
  channel: { type: string; directParticipants: { userId: string }[] },
  myId: string | null,
): boolean {
  return (
    channel.type === 'DM' &&
    channel.directParticipants.length === 1 &&
    channel.directParticipants[0]?.userId === myId
  );
}

export function directChannelLabel(channel: DirectChannel, myId: string | null): string {
  if (isSelfVault(channel, myId)) return i18n.t('ui.saved.title');
  // `directParticipants` should always be present, but guard it: a partial
  // channel object (mid-update store entry / a slimmer payload) otherwise
  // crashes the whole DM sidebar via the error boundary.
  const participants = channel.directParticipants ?? [];
  if (channel.type === 'GROUP_DM') {
    if (channel.name) return channel.name;
    return (
      participants
        .filter((participant) => participant.userId !== myId)
        .slice(0, 3)
        .map((participant) => participant.user.displayName || participant.user.username)
        .join(', ') || i18n.t('ui.dmLabel.group')
    );
  }
  const other = participants.find((participant) => participant.userId !== myId);
  return other?.user.displayName || other?.user.username || i18n.t('ui.dmLabel.dialog');
}
